import React, { useMemo } from 'react';
import SalesDetailPage from './SalesDetailPage';
import {
  LYBUNT_SYBUNT_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/lybuntSybuntColumnsConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';

const EMPTY_FUNDS = [];

const buildFilterGroups = (venueOwnerships, funds) => {
  const groups = [];

  if (venueOwnerships && venueOwnerships.length > 0) {
    groups.push({
      label: 'Venues',
      filters: [
        {
          id: 'venue_ownership_id',
          label: 'Venue',
          type: 'checkbox',
          options: venueOwnerships.map((vo) => ({
            value: String(vo.id),
            label: vo.name || vo.venue_name || `Venue ${vo.id}`,
          })),
        },
      ],
    });
  }

  if (funds.length > 0) {
    groups.push({
      label: 'Fund',
      filters: [
        {
          id: 'fund_id',
          label: 'Fund',
          type: 'checkbox',
          options: funds.map((fund) => ({ value: String(fund.id), label: fund.name })),
        },
      ],
    });
  }

  groups.push({
    label: 'Report Type',
    filters: [
      {
        id: 'report_type',
        label: 'Lapsed window',
        type: 'checkbox',
        options: [
          { value: 'lybunt', label: 'LYBUNT (gave last year, not this year)' },
          { value: 'sybunt', label: 'SYBUNT (gave 2+ years ago, not this year)' },
        ],
      },
    ],
  });

  return groups;
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  if (activeFilters.report_type && activeFilters.report_type.length > 0) {
    params.report_type = activeFilters.report_type;
  }
  if (activeFilters.fund_id && activeFilters.fund_id.length > 0) {
    params.fund_id = activeFilters.fund_id;
  }
  return params;
};

const LYBUNT_SYBUNT_CONFIG = {
  pageTitle: 'LYBUNT / SYBUNT',
  pageSubtitle: 'Lapsed donors who have not given this year, with last gift, lifetime giving, and a suggested ask. Fund filters apply to gift history and lapse status. Attributed donors only; anonymous gifts excluded.',
  sidebarKey: 'donations_lybunt_sybunt',
  defaultPreset: 'year_to_date',
  urlFilterIds: ['venue_ownership_id', 'report_type', 'fund_id'],
  dateModeOptions: [{ key: 'order_date', label: 'Order Date' }],
  showDatePicker: false,

  columns: LYBUNT_SYBUNT_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingDonationsLybuntSybuntColumns_${teamId}`,
  firstColumnId: 'donor_name',
  defaultSortRules: [{ field: 'last_gift_at', direction: 'desc' }],

  kpiFromDetailResponse: true,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/donations/lybunt-sybunt/data.json`,
  detailResponseKey: 'donors',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/donations/lybunt-sybunt/export`,
  detailErrorMessage: 'Failed to load lapsed donor data.',
  detailTableTitle: 'Lapsed Donors',
  emptyMessage: 'No lapsed donors for this selection.',
  searchPlaceholder: 'Search donors...',
  paginationLabel: 'donors',
  itemLabel: (count) => `donor${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,
  filterTitle: 'Filter Donors',
  filterSubtitle: 'Apply filters to narrow lapsed donors.',

  buildKpiCards: (current) => [
    { label: 'Lapsed Donors', value: (current.donor_count || 0).toLocaleString() },
    { label: 'Lifetime Giving', value: compactCurrency(current.lifetime_total || 0) },
    { label: 'Avg Last Gift', value: currencyFormatter.format(current.avg_last_gift || 0) },
    { label: 'Total Suggested Ask', value: compactCurrency(current.suggested_ask_total || 0) },
  ],
  kpiGridColumns: 4,

  renderCellValue: (col, row, { currencyFormatter: cf, formatEventDate, team }) => {
    switch (col.id) {
      case 'donor_name': {
        const name = row.donor_name || '(No name)';
        if (team?.package?.crm && row.id) {
          return (
            <a href={`/teams/${team.id}/crm/fans/${row.id}`} style={{ color: 'var(--sl-fg-brand, #1982c4)' }}>
              {name}
            </a>
          );
        }
        return name;
      }
      case 'email': return row.email || '—';
      case 'last_gift_at': return <span className="text-nowrap">{row.last_gift_at ? formatEventDate(row.last_gift_at) : '—'}</span>;
      case 'last_gift_amount': return cf.format(row.last_gift_amount || 0);
      case 'lifetime_total': return cf.format(row.lifetime_total || 0);
      case 'gift_count': return (row.gift_count || 0).toLocaleString();
      case 'suggested_ask': return cf.format(row.suggested_ask || 0);
      default: return '—';
    }
  },
};

function ReportingDonationsLybuntSybuntPage(props) {
  const funds = props.funds || EMPTY_FUNDS;
  const config = useMemo(
    () => ({ ...LYBUNT_SYBUNT_CONFIG, buildFilterGroups: (venues) => buildFilterGroups(venues, funds) }),
    [funds]
  );
  return <SalesDetailPage {...props} config={config} />;
}

export default ReportingDonationsLybuntSybuntPage;

